import { useState } from 'react';
import './ManualEntryModal.css';

type Props = {
  teamNames: string[];
  onClose: () => void;
  onSave: (data: Record<string, unknown>) => void;
};

export const ManualEntryModal = ({ teamNames, onClose, onSave }: Props) => {
  const [formData, setFormData] = useState({
    teamName: teamNames[0] || '',
    jerseyNumber: '',
    firstName: '',
    lastName: '',
    gradYear: '',
    position: '',
    bats: '',
    throws: '',
    height: '',
    weight: '',
    phone: '',
    email: '',
    city: '',
    state: '',
    school: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const data: Record<string, unknown> = {};

    // Convert form data to proper types
    if (formData.teamName) data.teamName = formData.teamName;
    if (formData.jerseyNumber) data.jerseyNumber = parseInt(formData.jerseyNumber, 10);
    if (formData.firstName) data.firstName = formData.firstName;
    if (formData.lastName) data.lastName = formData.lastName;
    if (formData.gradYear) data.gradYear = parseInt(formData.gradYear, 10);
    if (formData.position) data.position = formData.position;
    if (formData.bats) data.bats = formData.bats;
    if (formData.throws) data.throws = formData.throws;
    if (formData.height) data.height = formData.height;
    if (formData.weight) data.weight = parseInt(formData.weight, 10);
    if (formData.phone) data.phone = formData.phone;
    if (formData.email) data.email = formData.email;
    if (formData.city) data.city = formData.city;
    if (formData.state) data.state = formData.state;
    if (formData.school) data.school = formData.school;

    onSave(data);
  };

  return (
    <div className="manual-entry-modal__overlay" onClick={onClose}>
      <div className="manual-entry-modal" onClick={(e) => e.stopPropagation()}>
        <div className="manual-entry-modal__header">
          <h3 className="manual-entry-modal__title">Add Record Manually</h3>
          <button className="manual-entry-modal__close" onClick={onClose}>
            Close
          </button>
        </div>

        <form onSubmit={handleSubmit} className="manual-entry-modal__form">
          <div className="manual-entry-modal__row">
            <div className="manual-entry-modal__field">
              <label htmlFor="manual-entry-modal-team-0">Team</label>
              <select id="manual-entry-modal-team-0" name="teamName" value={formData.teamName} onChange={handleChange}>
                <option value="">Select team...</option>
                {teamNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
                <option value="__new__">+ Add new team</option>
              </select>
              {formData.teamName === '__new__' && (
                <input
                  type="text"
                  name="teamName"
                  placeholder="Enter team name"
                  onChange={handleChange}
                  className="manual-entry-modal__new-team"
                />
              )}
            </div>
            <div className="manual-entry-modal__field manual-entry-modal__field--small">
              <label htmlFor="manual-entry-modal-jersey-1">Jersey #</label>
              <input id="manual-entry-modal-jersey-1"
                type="number"
                name="jerseyNumber"
                value={formData.jerseyNumber}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="manual-entry-modal__row">
            <div className="manual-entry-modal__field">
              <label htmlFor="manual-entry-modal-first-name-2">First Name *</label>
              <input id="manual-entry-modal-first-name-2"
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="manual-entry-modal__field">
              <label htmlFor="manual-entry-modal-last-name-3">Last Name *</label>
              <input id="manual-entry-modal-last-name-3"
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="manual-entry-modal__row">
            <div className="manual-entry-modal__field manual-entry-modal__field--small">
              <label htmlFor="manual-entry-modal-grad-year-4">Grad Year</label>
              <input id="manual-entry-modal-grad-year-4"
                type="number"
                name="gradYear"
                value={formData.gradYear}
                onChange={handleChange}
                placeholder="2025"
              />
            </div>
            <div className="manual-entry-modal__field manual-entry-modal__field--small">
              <label htmlFor="manual-entry-modal-position-5">Position</label>
              <input id="manual-entry-modal-position-5"
                type="text"
                name="position"
                value={formData.position}
                onChange={handleChange}
              />
            </div>
            <div className="manual-entry-modal__field manual-entry-modal__field--small">
              <label htmlFor="manual-entry-modal-bats-6">Bats</label>
              <select id="manual-entry-modal-bats-6" name="bats" value={formData.bats} onChange={handleChange}>
                <option value="">-</option>
                <option value="R">R</option>
                <option value="L">L</option>
                <option value="S">S</option>
              </select>
            </div>
            <div className="manual-entry-modal__field manual-entry-modal__field--small">
              <label htmlFor="manual-entry-modal-throws-7">Throws</label>
              <select id="manual-entry-modal-throws-7" name="throws" value={formData.throws} onChange={handleChange}>
                <option value="">-</option>
                <option value="R">R</option>
                <option value="L">L</option>
              </select>
            </div>
          </div>

          <div className="manual-entry-modal__row">
            <div className="manual-entry-modal__field manual-entry-modal__field--small">
              <label htmlFor="manual-entry-modal-height-8">Height</label>
              <input id="manual-entry-modal-height-8"
                type="text"
                name="height"
                value={formData.height}
                onChange={handleChange}
                placeholder="6-1"
              />
            </div>
            <div className="manual-entry-modal__field manual-entry-modal__field--small">
              <label htmlFor="manual-entry-modal-weight-9">Weight</label>
              <input id="manual-entry-modal-weight-9"
                type="number"
                name="weight"
                value={formData.weight}
                onChange={handleChange}
                placeholder="180"
              />
            </div>
            <div className="manual-entry-modal__field">
              <label htmlFor="manual-entry-modal-school-10">School</label>
              <input id="manual-entry-modal-school-10"
                type="text"
                name="school"
                value={formData.school}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="manual-entry-modal__row">
            <div className="manual-entry-modal__field">
              <label htmlFor="manual-entry-modal-phone-11">Phone</label>
              <input id="manual-entry-modal-phone-11"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>
            <div className="manual-entry-modal__field">
              <label htmlFor="manual-entry-modal-email-12">Email</label>
              <input id="manual-entry-modal-email-12"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="manual-entry-modal__row">
            <div className="manual-entry-modal__field">
              <label htmlFor="manual-entry-modal-city-13">City</label>
              <input id="manual-entry-modal-city-13"
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
              />
            </div>
            <div className="manual-entry-modal__field manual-entry-modal__field--small">
              <label htmlFor="manual-entry-modal-state-14">State</label>
              <input id="manual-entry-modal-state-14"
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                maxLength={2}
              />
            </div>
          </div>

          <div className="manual-entry-modal__actions">
            <button
              type="button"
              className="manual-entry-modal__btn manual-entry-modal__btn--secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="manual-entry-modal__btn manual-entry-modal__btn--primary"
              disabled={!formData.firstName || !formData.lastName}
            >
              Add Record
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
